import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Star, X, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { listReviews, getMyReview, postReview } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

function fmt(iso) { try { return new Date(iso).toLocaleDateString(); } catch { return ''; } }

function StarRow({ value, onChange, size = 22, readOnly = false }) {
  return (
    <div className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(i)}
          className={readOnly ? 'cursor-default' : 'transition hover:scale-110'}
          data-testid={`review-star-${i}`}
          aria-label={`${i} star`}
        >
          <Star size={size} className={i <= value ? 'fill-[color:var(--gold)] text-[color:var(--gold)]' : 'text-zinc-700'} />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsModal({ open, onOpenChange, itemType, itemId, itemName }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    if (!itemType || !itemId) return;
    setLoading(true);
    try {
      const list = await listReviews(itemType, itemId);
      setData(list);
      if (user) {
        try {
          const m = await getMyReview(itemType, itemId);
          if (m) { setMine(m); setRating(m.rating); setComment(m.comment || ''); }
          else { setMine(null); setRating(5); setComment(''); }
        } catch { /* */ }
      } else {
        setMine(null);
      }
    } catch (e) { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) reload(); /* eslint-disable-next-line */ }, [open, itemId, user?.id]);

  const submit = async () => {
    if (!user) { toast.error('Please log in to write a review'); return; }
    if (rating < 1 || rating > 5) { toast.error('Pick 1–5 stars'); return; }
    setSubmitting(true);
    try {
      await postReview({ item_type: itemType, item_id: itemId, rating, comment });
      toast.success(mine ? 'Review updated' : 'Thanks for your review!');
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-[520px] max-h-[88vh] bg-[#0f0f0f] border border-zinc-800 text-zinc-100 p-0 rounded-2xl [&>button]:hidden overflow-hidden flex flex-col"
        data-testid="reviews-modal"
      >
        <DialogTitle className="sr-only">Reviews for {itemName}</DialogTitle>
        <DialogDescription className="sr-only">Read and write reviews and ratings.</DialogDescription>
        <button onClick={() => onOpenChange(false)} className="absolute right-3 top-3 z-20 w-9 h-9 rounded-full text-zinc-300 hover:text-white bg-black/40 hover:bg-red-500/80 border border-white/10 flex items-center justify-center" data-testid="reviews-close-btn"><X size={18} /></button>

        <div className="shrink-0 px-5 pt-5 pb-4 bg-gradient-to-br from-[color:var(--gold)]/15 via-zinc-950 to-zinc-950">
          <div className="text-zinc-300 text-[11px] uppercase tracking-wider mb-0.5">Reviews</div>
          <div className="text-lg font-bold text-zinc-100 truncate">{itemName}</div>
          <div className="mt-2 flex items-center gap-3">
            <StarRow value={Math.round(data.average || 0)} readOnly />
            <span className="text-zinc-300 text-sm font-bold tabular-nums">{(data.average || 0).toFixed(1)}</span>
            <span className="text-zinc-500 text-xs">({data.count} review{data.count === 1 ? '' : 's'})</span>
          </div>
        </div>

        {user && (
          <div className="shrink-0 border-y border-zinc-800 bg-zinc-950/60 px-5 py-4">
            <div className="text-zinc-300 text-xs font-bold mb-1.5">{mine ? 'Edit your review' : 'Write a review'}</div>
            <StarRow value={rating} onChange={setRating} />
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 600))}
              placeholder="Share your experience…"
              className="mt-2 w-full min-h-[64px] max-h-[120px] bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100"
              data-testid="review-comment-input"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-zinc-500 text-[11px]">{comment.length}/600</span>
              <button onClick={submit} disabled={submitting} className="h-9 px-3 rounded-md bg-[color:var(--gold)] hover:brightness-110 text-black font-bold text-xs flex items-center gap-1.5 disabled:opacity-60" data-testid="review-submit-btn">
                <Send size={13} /> {submitting ? 'Saving…' : mine ? 'Update' : 'Submit'}
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto chat-scroll p-3 sm:p-4 space-y-2 min-h-0">
          {loading ? (
            <div className="text-zinc-500 text-sm p-4 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : data.reviews.length === 0 ? (
            <div className="text-zinc-500 text-sm p-6 text-center">No reviews yet. {user ? 'Be the first!' : 'Log in to write the first review.'}</div>
          ) : (
            data.reviews.map((r) => (
              <div key={r.id} className="rounded-lg bg-zinc-950 border border-zinc-800 p-3" data-testid={`review-item-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-zinc-100 font-bold text-sm truncate">{r.user_name}</span>
                  <StarRow value={r.rating} readOnly size={14} />
                </div>
                {r.comment && <div className="text-zinc-300 text-sm mt-1 whitespace-pre-wrap">{r.comment}</div>}
                <div className="text-zinc-600 text-[10px] mt-1">{fmt(r.created_at)}</div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
